import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { DISCOUNT_TYPE } from '@baemingo/table-events/dist/types';
import React, { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { useTableApi } from '~Components/TableUniverse';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import { v4 as uuid } from 'uuid';
import { BookingStatus } from '../../../__queries__';
import {
  GetBookings,
  GetBookings_operativeViewer_bookingsForVenueAndDate,
  GetBookingsVariables,
  GetIncomingBookings,
  GetIncomingBookings_operativeViewer_incomingBookingsForVenue,
  GetIncomingBookingsVariables,
  IsVenueBookingsEnabled,
  MoveBookingToTable,
  MoveBookingToTableVariables,
  UpdateBookingStatus,
  UpdateBookingStatusVariables,
} from './api/__queries__';
import { BOOKINGS_QUERY, INCOMING_BOOKINGS, VENUE_BOOKINGS_ENABLED_QUERY } from './api/queries';
import { MOVE_BOOKING_TO_TABLE, UPDATE_BOOKING_STATUS } from './api/mutations';
import * as actions from '~actions';
import useI18n from '~Translations';
import { GetAllTables, GetAllTablesVariables } from './__queries__';

export const BOOKINGS_DISCOUNT_ID = 'BOOKINGS_DISCOUNT_ID';

const GET_ALL_TABLES = gql`
  query GetAllTables($venueId: ID!) {
    Venue(id: $venueId) {
      tables {
        id
        code
      }
    }
  }
`;

interface BookingsContextType {
  // If bookings are activated for the venue at all, this can be used to conditionally show/hide booking related features in the app and avoid unnecessary queries and logic when bookings are not used
  enabled: boolean;
  baemingoChoiceEnabled: boolean; // Whether the Baemingo Choice feature is enabled, which allows guests to choose their own table. This is used to conditionally show/hide Baemingo Choice related features in the UI
  defaultDurationMinutes: number; // Default duration for bookings without an end time, used to calculate the booking block length in the UI
  autoAcceptBookings: boolean; // Whether incoming bookings are automatically accepted, used to conditionally show/hide incoming booking actions in the UI

  // Bookings for today, used in
  today: GetBookings_operativeViewer_bookingsForVenueAndDate[];
  todayLoading: boolean;

  // Incoming bookings, will be polled every minute to check for new bookings. This is used to show real-time incoming bookings in the UI
  incoming: GetIncomingBookings_operativeViewer_incomingBookingsForVenue[];
  incomingLoading: boolean;

  // Highlighted incoming booking ID for bidirectional highlighting between sidebar and grid
  highlightedIncomingBookingId: string | null;

  // Function to refetch bookings for a specific date, used in the calendar view
  fetchBookingsForDate: (
    date: string,
  ) => Promise<GetBookings_operativeViewer_bookingsForVenueAndDate[]>;

  actions: {
    // Incoming booking actions
    accept: (bookingId: string, date: string) => Promise<boolean>;
    reject: (bookingId: string, date: string) => Promise<boolean>;

    // Todays booking actions
    start: (bookingId: string, tableId: string) => Promise<string | null>;
    finish: (bookingId: string) => Promise<void>;

    // Calendar view actions
    moveToAnotherTable: (bookingId: string, tableIds: string[], date: string) => Promise<boolean>;
    cancel: (bookingId: string, date: string) => Promise<boolean>;

    // Highlight actions
    setHighlightedIncomingBooking: (bookingId: string | null) => void;
    toggleHighlightIncomingBooking: (bookingId: string) => void;
    //TODO: add no-show action
  };
}

const BookingsContext = createContext<BookingsContextType | undefined>(undefined);

interface BookingsProviderProps {
  children: ReactNode;
}

export const BookingsProvider: React.FC<BookingsProviderProps> = ({ children }) => {
  const venueId = useSelector((state) => state.venueId);
  const logger = useLogger('bookings-today');
  const tableApi = useTableApi();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const { i18n } = useI18n();

  const allTablesQuery = useQuery<GetAllTables, GetAllTablesVariables>(GET_ALL_TABLES, {
    variables: { venueId: venueId || '' },
    skip: !venueId,
  });

  // State for highlighted incoming booking (for bidirectional highlighting between sidebar and grid)
  const [highlightedIncomingBookingId, setHighlightedIncomingBookingId] = React.useState<
    string | null
  >(null);

  const bookingsEnabledQuery = useQuery<IsVenueBookingsEnabled>(VENUE_BOOKINGS_ENABLED_QUERY);
  const bookingsAreEnabledForVenue =
    bookingsEnabledQuery.data?.operativeViewer?.bookingsEnabled ?? false;
  const baemingoChoiceEnabled =
    bookingsEnabledQuery.data?.operativeViewer?.baemingoChoiceEnabled ?? false;

  // Get today's date in YYYY-MM-DD format
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(today.getDate()).padStart(2, '0')}`;

  const todaysBookingsQuery = useQuery<GetBookings, GetBookingsVariables>(BOOKINGS_QUERY, {
    variables: {
      venueId: venueId || '',
      date: todayString,
    },
    skip: !venueId || !bookingsAreEnabledForVenue,
    fetchPolicy: 'cache-and-network', // Use cache-and-network to show cached bookings immediately and update with fresh data when it arrives
  });

  const incomingBookingsQuery = useQuery<GetIncomingBookings, GetIncomingBookingsVariables>(
    INCOMING_BOOKINGS,
    {
      variables: { venueId: venueId || '' },
      skip: !venueId || !bookingsAreEnabledForVenue,
      pollInterval: 60000, // Poll every 60 seconds for real-time updates
    },
  );

  const [updateBookingStatus] = useMutation<UpdateBookingStatus, UpdateBookingStatusVariables>(
    UPDATE_BOOKING_STATUS,
  );

  const [moveBookingToTableMutation] = useMutation<
    MoveBookingToTable,
    MoveBookingToTableVariables
  >(MOVE_BOOKING_TO_TABLE);

  const todaysBookings = useMemo(() => {
    return todaysBookingsQuery.data?.operativeViewer?.bookingsForVenueAndDate || [];
  }, [todaysBookingsQuery.data]);

  // Filter incoming bookings to only have those which have not past the start date and time + a buffer for 1h
  // 1 hour buffer to allow for near bookings with a late arrival
  const incoming = useMemo(() => {
    const bookings = incomingBookingsQuery.data?.operativeViewer?.incomingBookingsForVenue || [];

    return bookings.filter((booking) => {
      const bookingDateTime = new Date(`${booking.date}T${booking.startTime}`);
      const nowWithBuffer = new Date(Date.now() - 60 * 60 * 1000);

      return bookingDateTime >= nowWithBuffer;
    });
  }, [incomingBookingsQuery.data]);

  const fetchBookingsForDate = useCallback(
    async (date: string): Promise<GetBookings_operativeViewer_bookingsForVenueAndDate[]> => {
      if (!venueId || !bookingsAreEnabledForVenue) return [];

      const res = await client.query<GetBookings, GetBookingsVariables>({
        query: BOOKINGS_QUERY,
        variables: { venueId, date },
        fetchPolicy: 'network-only',
      });

      return res.data?.operativeViewer?.bookingsForVenueAndDate ?? [];
    },
    [client, venueId, bookingsAreEnabledForVenue],
  );

  const onStartBooking = useCallback(
    async (bookingId: string, tableId: string): Promise<string | null> => {
      try {
        logger.info(`Starting booking ${bookingId} on table ${tableId}`);
        const newChequeId = uuid();

        const res = await updateBookingStatus({
          variables: {
            bookingId,
            status: BookingStatus.STARTED,
            chequeId: newChequeId,
          },
        });

        const affectedBooking = res.data?.updateBookingStatus.booking;

        if (!affectedBooking) {
          throw new Error(i18n('Bookings.ErrorNoBookingReturned'));
        }

        // If we just started a booking, open a cheque on the tableId
        // Create a new cheque with the booking's party size as coverage
        tableApi.createCheque(tableId, affectedBooking.partySize, newChequeId);
        tableApi.log.createCheque(newChequeId, tableId, affectedBooking.partySize);

        await todaysBookingsQuery.refetch();

        // To ensure we apply the following events after the creation of the cheque we add a timeout
        setTimeout(() => {
          tableApi.setChequeName(newChequeId, affectedBooking.guestName);
          tableApi.log.setChequeName(newChequeId, affectedBooking.guestName);
        }, 10);

        setTimeout(() => {
          tableApi.setChequeCoverage(newChequeId, affectedBooking.partySize);
          tableApi.log.setChequeCoverage(newChequeId, affectedBooking.partySize);
        }, 15);

        // Apply booking discount to the cheque if present
        if (affectedBooking.discountPercent && affectedBooking.discountPercent > 0) {
          // Increase timeout to ensure cheque is created in the universe state
          setTimeout(() => {
            logger.info(
              `Applying ${affectedBooking.discountPercent}% discount to cheque ${newChequeId}`,
            );
            try {
              tableApi.applyDiscount(newChequeId, {
                offerId: BOOKINGS_DISCOUNT_ID,
                type: DISCOUNT_TYPE.PERCENTAGE,
                rate: affectedBooking.discountPercent,
                selectedItemIds: [], // Empty array means the discount is applied to the whole cheque
                applyPersistentDiscount: true,
              });
              logger.info(
                `Successfully applied ${affectedBooking.discountPercent}% discount to cheque ${newChequeId}`,
              );
            } catch (error) {
              logger.error('Failed to apply discount to cheque', {
                errorMessage: error instanceof Error ? error.message : String(error),
                chequeId: newChequeId,
                discountPercent: affectedBooking.discountPercent,
              });
            }
          }, 100); // Increased timeout to ensure cheque exists in state
        }

        logger.info(`Successfully started booking ${bookingId}`, { chequeId: newChequeId });
        return newChequeId;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Error starting booking', { bookingId, tableId, errorMessage });
        dispatch(actions.addToast(i18n('Bookings.ErrorStartingBooking'), true));

        return null;
      }
    },
    [updateBookingStatus, logger, tableApi, todaysBookingsQuery],
  );

  const onFinishBooking = useCallback(
    async (chequeId: string): Promise<void> => {
      const foundBooking = todaysBookings.find((b) => b.chequeId === chequeId);

      // This cheque was not part of a booking, return early
      if (!foundBooking) {
        return;
      }

      try {
        logger.info(`Finishing booking ${foundBooking.id} for cheque ${chequeId}`);

        await updateBookingStatus({
          variables: {
            bookingId: foundBooking.id,
            status: BookingStatus.FINISHED,
          },
        });

        await todaysBookingsQuery.refetch();
        logger.info(`Successfully finished booking ${foundBooking.id}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Error finishing booking', {
          bookingId: foundBooking.id,
          chequeId,
          errorMessage,
        });
        dispatch(actions.addToast(i18n('Bookings.ErrorFinishingBooking'), true));
      }
    },
    [updateBookingStatus, logger, todaysBookingsQuery, todaysBookings],
  );

  const onAcceptBooking = async (bookingId: string, date: string) => {
    try {
      logger.info('Accepting booking', { bookingId, date });

      const result = await updateBookingStatus({
        variables: {
          bookingId: bookingId,
          status: BookingStatus.ACCEPTED,
        },
      });

      if (result.data?.updateBookingStatus?.success) {
        logger.info('Booking accepted successfully', {
          bookingId: bookingId,
        });

        await incomingBookingsQuery.refetch();

        if (date === todayString) {
          await todaysBookingsQuery.refetch();
        }

        return true;
      } else {
        const errorMessage = result.data?.updateBookingStatus?.error || 'Unknown error';
        logger.error('Failed to accept booking', {
          bookingId: bookingId,
          errorMessage,
        });

        dispatch(actions.addToast(i18n('Bookings.ErrorAcceptingBooking'), true));

        return false;
      }
    } catch (error) {
      logger.error('Error accepting booking', {
        bookingId: bookingId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      dispatch(actions.addToast(i18n('Bookings.ErrorAcceptingBooking'), true));
      return false;
    }
  };

  const onRejectBooking = async (bookingId: string, date: string) => {
    try {
      const result = await updateBookingStatus({
        variables: {
          bookingId: bookingId,
          status: BookingStatus.DENIED,
        },
      });

      if (result.data?.updateBookingStatus?.success) {
        logger.info('Booking denied successfully', {
          bookingId: bookingId,
        });

        await incomingBookingsQuery.refetch();

        if (date === todayString) {
          await todaysBookingsQuery.refetch();
        }

        return true;
      } else {
        const errorMessage = result.data?.updateBookingStatus?.error || 'Unknown error';
        logger.error('Failed to deny booking', {
          bookingId: bookingId,
          errorMessage,
        });

        dispatch(actions.addToast(i18n('Bookings.ErrorRejectingBooking'), true));
        return false;
      }
    } catch (error) {
      logger.error('Error denying booking', {
        bookingId: bookingId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      dispatch(actions.addToast(i18n('Bookings.ErrorRejectingBooking'), true));
      return false;
    }
  };

  const onMoveToAnotherTable = async (
    bookingId: string,
    tableIds: string[],
    date: string,
  ): Promise<boolean> => {
    try {
      logger.info('Moving booking to another table', {
        bookingId,
        tableIds,
      });

      const result = await moveBookingToTableMutation({
        variables: {
          bookingId: bookingId,
          tableIds,
        },
        refetchQueries: venueId
          ? [
              {
                query: BOOKINGS_QUERY,
                variables: {
                  venueId,
                  date,
                },
              },
            ]
          : [],
      });

      if (result.data?.moveBookingToTable?.success) {
        logger.info('Booking moved to another table successfully', {
          bookingId,
          tableIds,
        });

        // Refetch today's bookings if moving today's booking
        if (date === todayString) {
          await todaysBookingsQuery.refetch();
        }

        return true;
      } else {
        const errorMessage = result.data?.moveBookingToTable?.error || 'Unknown error';
        logger.error('Failed to move booking', {
          bookingId,
          errorMessage,
        });

        if (errorMessage.includes('ERROR_TABLE_NOT_FOUND')) {
          const movedTableId = errorMessage.split('ERROR_TABLE_NOT_FOUND:')[1]?.trim();
          const foundTable = allTablesQuery.data?.Venue.tables.find((t) => t.id === movedTableId);

          const movedTableCode = foundTable?.code || movedTableId;

          dispatch(
            actions.addToast(
              i18n('Bookings.ErrorMovingBookingTableNotFound', movedTableCode),
              true,
            ),
          );
        } else {
          dispatch(actions.addToast(i18n('Bookings.ErrorMovingBooking'), true));
        }

        return false;
      }
    } catch (error) {
      logger.error('Error moving booking to another table', {
        bookingId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      dispatch(actions.addToast(i18n('Bookings.ErrorMovingBooking'), true));

      return false;
    }
  };

  const onCancelBooking = async (bookingId: string, date: string): Promise<boolean> => {
    try {
      logger.info('Canceling booking', { bookingId });

      const result = await updateBookingStatus({
        variables: {
          bookingId,
          status: BookingStatus.CANCELED,
        },
        refetchQueries: venueId
          ? [
              {
                query: BOOKINGS_QUERY,
                variables: {
                  venueId,
                  date,
                },
              },
            ]
          : [],
      });

      if (result.data?.updateBookingStatus?.success) {
        logger.info('Booking canceled successfully', {
          bookingId,
        });

        // Refetch today's bookings if canceling today's booking
        if (date === todayString) {
          await todaysBookingsQuery.refetch();
        }

        return true;
      } else {
        const errorMessage = result.data?.updateBookingStatus?.error || 'Unknown error';
        logger.error('Failed to cancel booking', {
          bookingId,
          errorMessage,
        });
        dispatch(actions.addToast(i18n('Bookings.ErrorCancelingBooking'), true));

        return false;
      }
    } catch (error) {
      logger.error('Error canceling booking', {
        bookingId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      dispatch(actions.addToast(i18n('Bookings.ErrorCancelingBooking'), true));

      return false;
    }
  };

  // Toggle highlight for incoming booking
  const toggleHighlightIncomingBooking = React.useCallback((bookingId: string) => {
    setHighlightedIncomingBookingId((current) => (current === bookingId ? null : bookingId));
  }, []);

  const bookingsActions = {
    start: onStartBooking,
    finish: onFinishBooking,
    accept: onAcceptBooking,
    reject: onRejectBooking,
    cancel: onCancelBooking,
    moveToAnotherTable: onMoveToAnotherTable,
    setHighlightedIncomingBooking: setHighlightedIncomingBookingId,
    toggleHighlightIncomingBooking,
  };

  const value = {
    enabled: bookingsAreEnabledForVenue,
    baemingoChoiceEnabled: baemingoChoiceEnabled,
    defaultDurationMinutes:
      bookingsEnabledQuery.data?.operativeViewer?.defaultSeatingDurationMinutes ?? 120, // Default to 120 minutes if not specified
    autoAcceptBookings: bookingsEnabledQuery.data?.operativeViewer?.autoAcceptBookings ?? true, // Default to true if not specified
    today: todaysBookings,
    todayLoading: todaysBookingsQuery.loading,
    incoming,
    incomingLoading: incomingBookingsQuery.loading,
    highlightedIncomingBookingId,
    fetchBookingsForDate,
    actions: bookingsActions,
  };

  return <BookingsContext.Provider value={value}>{children}</BookingsContext.Provider>;
};

// Hook to use the BookingsContext
export const useBookings = (): BookingsContextType => {
  const context = useContext(BookingsContext);

  if (!context) {
    throw new Error('useBookings must be used within a BookingsProvider');
  }
  return context;
};

export default BookingsProvider;
